import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AuthShell, { GoogleAuthButton } from "@/components/AuthShell";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Signin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err, "Invalid email or password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to continue tracking your cases."
      footer={
        <>
          New to FellowLog?{" "}
          <Link to="/signup" className="text-neu-accent font-semibold" data-testid="signin-to-signup">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-5" data-testid="signin-form">
        <div>
          <label className="text-sm font-medium block mb-2">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="neu-input"
            data-testid="signin-email"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="text-sm font-medium block mb-2">Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="neu-input"
            data-testid="signin-password"
            autoComplete="current-password"
          />
        </div>
        {error && (
          <div className="text-sm text-red-600 bg-red-50 p-3 rounded-xl" data-testid="signin-error">
            {error}
          </div>
        )}
        <button type="submit" disabled={loading} className="btn-accent w-full" data-testid="signin-submit">
          {loading ? "Signing in…" : "Sign in"}
        </button>

        <div className="text-right">
          <Link to="/forgot-password" className="text-sm subtle hover:text-neu-accent" data-testid="signin-forgot">
            Forgot password?
          </Link>
        </div>

        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-neu-muted/30" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-[#E4EBF1] px-3 subtle">or</span>
          </div>
        </div>
        <GoogleAuthButton testId="signin-google" />
      </form>
    </AuthShell>
  );
}
